import React from 'react';
import { css } from '@emotion/css';

const policyPage = css({
  width: '100%',
  maxWidth: '1200px',
  margin: '0 auto',
  padding: 'var(--spacing-lg)',
  minHeight: 'calc(100vh - 100px)',
});

const policyPageContainer = css({
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--spacing-lg)',
});

const policyPageHeader = css({
  marginBottom: 'var(--spacing-lg)',
  borderBottom: '2px solid var(--pc-200)',
  paddingBottom: 'var(--spacing-md)',
});

const policyPageTitle = css({
  fontSize: 'var(--fs-800)',
  fontWeight: 700,
  color: 'var(--pc-700)',
  marginBottom: 'var(--spacing-xs)',
});

const policyPageSubtitle = css({
  fontSize: 'var(--fs-500)',
  color: 'var(--g-700)',
  fontWeight: 400,
});

const policyPageContent = css({
  fontSize: 'var(--fs-400)',
  lineHeight: 1.6,
  color: 'var(--g-800)',
});

const policyPageSection = css({
  marginBottom: 'var(--spacing-lg)',
});

const policyPageSectionTitle = css({
  fontSize: 'var(--fs-600)',
  fontWeight: 600,
  color: 'var(--pc-600)',
  marginBottom: 'var(--spacing-sm)',
  paddingBottom: 'var(--spacing-xs)',
  borderBottom: '1px solid var(--pc-100)',
});

const policyPageList = css({
  listStyleType: 'disc',
  paddingLeft: 'var(--spacing-lg)',
  marginBottom: 'var(--spacing-md)',
});

const policyPageListItem = css({
  marginBottom: 'var(--spacing-xs)',
});

const policyPageParagraph = css({
  marginBottom: 'var(--spacing-md)',
});

// Media queries for responsiveness
const responsiveStyles = css({
  '@media (max-width: 768px)': {
    [`&.${policyPage}`]: {
      padding: 'var(--spacing-md)',
    },
    [`.${policyPageTitle}`]: {
      fontSize: 'var(--fs-700)',
    },
    [`.${policyPageSubtitle}`]: {
      fontSize: 'var(--fs-450)',
    },
    [`.${policyPageSectionTitle}`]: {
      fontSize: 'var(--fs-500)',
    },
  },
  '@media (max-width: 480px)': {
    [`&.${policyPage}`]: {
      padding: 'var(--spacing-sm)',
    },
    [`.${policyPageTitle}`]: {
      fontSize: 'var(--fs-600)',
    },
    [`.${policyPageSubtitle}`]: {
      fontSize: 'var(--fs-400)',
    },
    [`.${policyPageContent}`]: {
      fontSize: 'var(--fs-350)',
    },
    [`.${policyPageSectionTitle}`]: {
      fontSize: 'var(--fs-450)',
    },
  },
});

export const TermsOfUse: React.FC = () => {
  return (
    <div className={`${policyPage} ${responsiveStyles}`}>
      <div className={policyPageContainer}>
        <div className={policyPageHeader}>
          <h1 className={policyPageTitle}>Termos e Condições</h1>
        </div>

        <div className={policyPageContent}>
          <div className={policyPageSection}>
            <h3 className={policyPageSectionTitle}>1. Termos</h3>
            <p className={policyPageParagraph}>
              Ao acessar o site Anchor Experiences, concorda em cumprir estes termos de serviço, todas as 
              leis e regulamentos aplicáveis ​​e concorda que é responsável pelo cumprimento de todas as 
              leis locais aplicáveis. Se você não concordar com algum desses termos, está proibido de usar 
              ou acessar este site. Os materiais contidos neste site são protegidos pelas leis de direitos 
              autorais e marcas comerciais aplicáveis.
            </p>
          </div>

          <div className={policyPageSection}>
            <h3 className={policyPageSectionTitle}>2. Uso de Licença</h3>
            <p className={policyPageParagraph}>
              É concedida permissão para baixar temporariamente uma cópia dos materiais (informações ou software) 
              no site Anchor Experiences, apenas para visualização transitória pessoal e não comercial. Esta é a 
              concessão de uma licença, não uma transferência de título e, sob esta licença, você não pode:
            </p>
            <ul className={policyPageList}>
              <li className={policyPageListItem}>modificar ou copiar os materiais;</li>
              <li className={policyPageListItem}>
                usar os materiais para qualquer finalidade comercial ou para exibição pública 
                (comercial ou não comercial);
              </li>
              <li className={policyPageListItem}>
                tentar descompilar ou fazer engenharia reversa de qualquer software contido no site 
                Anchor Experiences;
              </li>
              <li className={policyPageListItem}>
                remover quaisquer direitos autorais ou outras notações de propriedade dos materiais; ou
              </li>
              <li className={policyPageListItem}>
                transferir os materiais para outra pessoa ou 'espelhe' os materiais em qualquer outro servidor.
              </li>
            </ul>
            <p className={policyPageParagraph}>
              Esta licença será automaticamente rescindida se você violar alguma dessas restrições e poderá 
              ser rescindida pela Anchor Experiences a qualquer momento. Ao encerrar a visualização desses 
              materiais ou após o término desta licença, você deve apagar todos os materiais baixados em sua 
              posse, seja em formato eletrónico ou impresso.
            </p>
          </div>

          <div className={policyPageSection}>
            <h3 className={policyPageSectionTitle}>3. Isenção de responsabilidade</h3>
            <p className={policyPageParagraph}>
              Os materiais no site Anchor Experiences são fornecidos 'como estão'. A Anchor Experiences não 
              oferece garantias, expressas ou implícitas, e, por este meio, isenta e nega todas as outras 
              garantias, incluindo, sem limitação, garantias implícitas ou condições de comercialização, 
              adequação a um fim específico ou não violação de propriedade intelectual ou outra violação de direitos.
            </p>
            <p className={policyPageParagraph}>
              Além disso, Anchor Experiences não garante ou faz qualquer representação relativa à precisão, 
              aos resultados prováveis ​​ou à confiabilidade do uso dos materiais em seu site ou de outra 
              forma relacionado a esses materiais ou em sites vinculados a este site.
            </p>
          </div>

          <div className={policyPageSection}>
            <h3 className={policyPageSectionTitle}>4. Limitações</h3>
            <p className={policyPageParagraph}>
              Em nenhum caso Anchor Experiences ou seus fornecedores serão responsáveis ​​por quaisquer danos 
              (incluindo, sem limitação, danos por perda de dados ou lucro ou devido a interrupção dos negócios) 
              decorrentes do uso ou da incapacidade de usar os materiais no site Anchor Experiences, mesmo que 
              a Anchor Experiences ou um representante autorizado da mesma tenha sido notificado oralmente ou 
              por escrito da possibilidade de tais danos. Como algumas jurisdições não permitem limitações em 
              garantias implícitas, ou limitações de responsabilidade por danos conseqüentes ou incidentais, 
              essas limitações podem não se aplicar a você.
            </p>
          </div>

          <div className={policyPageSection}>
            <h3 className={policyPageSectionTitle}>5. Precisão dos materiais</h3>
            <p className={policyPageParagraph}>
              Os materiais exibidos no site Anchor Experiences podem incluir erros técnicos, tipográficos 
              ou fotográficos. Anchor Experiences não garante que qualquer material em seu site seja preciso, 
              completo ou atual. Anchor Experiences pode fazer alterações nos materiais contidos em seu site 
              a qualquer momento, sem aviso prévio. No entanto, Anchor Experiences não se compromete a 
              atualizar os materiais.
            </p>
          </div>

          <div className={policyPageSection}>
            <h3 className={policyPageSectionTitle}>6. Links</h3>
            <p className={policyPageParagraph}>
              A Anchor Experiences não analisou todos os sites vinculados ao seu site e não é responsável 
              pelo conteúdo de nenhum site vinculado. A inclusão de qualquer link não implica endosso por 
              Anchor Experiences do site. O uso de qualquer site vinculado é por conta e risco do usuário.
            </p>
          </div>

          <div className={policyPageSection}>
            <h3 className={policyPageSectionTitle}>Modificações</h3>
            <p className={policyPageParagraph}>
              A Anchor Experiences pode revisar estes termos de serviço do site a qualquer momento, sem 
              aviso prévio. Ao usar este site, você concorda em ficar vinculado à versão atual desses 
              termos de serviço.
            </p>
          </div>

          <div className={policyPageSection}>
            <h3 className={policyPageSectionTitle}>Lei aplicável</h3>
            <p className={policyPageParagraph}>
              Estes termos e condições são regidos e interpretados de acordo com as leis da Anchor 
              Experiences e você se submete irrevogavelmente à jurisdição exclusiva dos tribunais 
              naquele estado ou localidade.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsOfUse;
